/**
 * PatternIQ Copilot Feedback Table
 * =================================
 * Paginated table of all feedback submissions with filters
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThumbsUp, ThumbsDown, Minus, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import ConversationReplay from './ConversationReplay';

export default function FeedbackTable() {
  const [page, setPage] = useState(1);
  const [rating, setRating] = useState('all');
  const [functionFilter, setFunctionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  // Fetch feedback data with real-time updates
  const { data, isLoading } = useQuery({
    queryKey: ['copilot-feedback-list', page, rating, functionFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        rating,
      });
      
      if (functionFilter) params.append('function', functionFilter);
      if (search) params.append('search', search);

      const response = await fetch(`/api/copilot/feedback/list?${params}`);
      if (!response.ok) throw new Error('Failed to fetch feedback');
      const result = await response.json();
      return result.data;
    },
    refetchInterval: 5000, // 5 seconds for real-time
    staleTime: 0,
  });

  // Get list of all functions for filter dropdown
  const { data: functionsList } = useQuery({
    queryKey: ['copilot-functions-list'],
    queryFn: async () => {
      // Extract unique function names from feedback
      const response = await fetch('/api/copilot/feedback/analytics?days=90');
      const result = await response.json();
      return result.data?.functionPerformance?.map((f: any) => f.functionName) || [];
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleOpenConversation = (conversationId: string, messageId: string) => {
    setSelectedConversation(conversationId);
    setSelectedMessageId(messageId);
  };

  if (isLoading && !data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const feedback = data?.feedback || [];
  const pagination = data?.pagination || {};

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Feedback Submissions</CardTitle>
          <CardDescription>
            All copilot feedback with filters and search
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Rating</label>
              <select 
                value={rating} 
                onChange={(e) => { setRating(e.target.value); setPage(1); }}
                className="w-full h-9 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <option value="all">All Ratings</option>
                <option value="positive">👍 Positive</option>
                <option value="negative">👎 Negative</option>
                <option value="unrated">— Unrated</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Function</label>
              <select 
                value={functionFilter} 
                onChange={(e) => { setFunctionFilter(e.target.value); setPage(1); }}
                className="w-full h-9 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <option value="">All Functions</option>
                {functionsList?.map((func: string) => (
                  <option key={func} value={func}>{func}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-2 block">Search</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search conversation ID or user email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} variant="secondary">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-3 font-medium">Date</th>
                  <th className="text-left py-3 px-3 font-medium">User</th>
                  <th className="text-left py-3 px-3 font-medium">Function</th>
                  <th className="text-center py-3 px-3 font-medium">Rating</th>
                  <th className="text-left py-3 px-3 font-medium">Feedback</th>
                  <th className="text-right py-3 px-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedback.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-500">
                      No feedback found
                    </td>
                  </tr>
                ) : (
                  feedback.map((item: any) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-3">
                        {new Date(item.createdAt).toLocaleDateString()}<br />
                        <span className="text-xs text-gray-500">
                          {new Date(item.createdAt).toLocaleTimeString()}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-xs">{item.userId}</td>
                      <td className="py-3 px-3">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {item.functionCalled || 'N/A'}
                        </code>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {item.rating === 1 ? (
                          <ThumbsUp className="h-5 w-5 text-green-600 inline" />
                        ) : item.rating === -1 ? (
                          <ThumbsDown className="h-5 w-5 text-red-600 inline" />
                        ) : (
                          <Minus className="h-5 w-5 text-gray-400 inline" />
                        )}
                      </td>
                      <td className="py-3 px-3 max-w-xs">
                        {item.feedbackText ? (
                          <span className="text-sm">
                            {item.feedbackText.length > 100
                              ? item.feedbackText.substring(0, 100) + '...'
                              : item.feedbackText}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No text feedback</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenConversation(item.conversationId, item.messageId)}
                        >
                          View Conversation
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation Replay Modal */}
      {selectedConversation && (
        <ConversationReplay
          conversationId={selectedConversation}
          messageId={selectedMessageId || undefined}
          onClose={() => {
            setSelectedConversation(null);
            setSelectedMessageId(null);
          }}
        />
      )}
    </>
  );
}

